import { useState } from 'react';
import toast from 'react-hot-toast';
import { Badge } from '../ui/Badge';
import { ConnectModal } from '../guide/ConnectModal';
import { ConnectButton } from '../guide/ConnectButton';
import { PluggedIcon } from '../guide/PlugIcon';
import { copyText } from '../guide/copyText';
import { KeyDeviceBlock } from './KeyDeviceBlock';
import type { VPNKey } from '../../types/vpnkey';
import { renameKey, unbindKeyDevice } from '../../api/vpn.api';
import { getApiErrorMessage, getApiErrorStatus } from '../../utils/apiError';
import styles from './VPNKeyDisplay.module.css';

interface VPNKeyDisplayProps {
  vpnKey: VPNKey;
  onRenamed?: (key: VPNKey) => void;
  /** Устройство отвязано: получает ключ с device = null для точечного обновления списка */
  onDeviceUnbound?: (key: VPNKey) => void;
}

export function VPNKeyDisplay({ vpnKey, onRenamed, onDeviceUnbound }: VPNKeyDisplayProps) {
  const [copied, setCopied] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [autoCopied, setAutoCopied] = useState(false);
  const [unbinding, setUnbinding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(vpnKey.subscriptionUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = vpnKey.subscriptionUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleUnbind = async () => {
    const confirmed = confirm(
      'Ключ перестанет работать на текущем устройстве. ' +
        'Чтобы подключить другое, вставьте ключ в приложение на нём заново.\n\nОтвязать?',
    );
    if (!confirmed) return;
    setUnbinding(true);
    try {
      await unbindKeyDevice(vpnKey.id);
      onDeviceUnbound?.({ ...vpnKey, device: null });
      toast.success('Устройство отвязано. Вставьте ключ в приложение на нужном устройстве.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Не удалось отвязать устройство. Попробуйте позже'));
    } finally {
      setUnbinding(false);
    }
  };

  const startEdit = () => {
    setNameDraft(vpnKey.deviceName || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const handleSaveName = async () => {
    const trimmed = nameDraft.trim();
    if (trimmed.length < 1 || trimmed.length > 50) {
      toast.error('Название должно быть от 1 до 50 символов');
      return;
    }
    if (trimmed === vpnKey.deviceName) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await renameKey(vpnKey.id, trimmed);
      onRenamed?.(updated);
      setEditing(false);
    } catch (err) {
      // Запасной текст по статусу — на случай ответа без русского сообщения
      const status = getApiErrorStatus(err);
      const fallback =
        status === 404
          ? 'Ключ не найден'
          : status === 422
            ? 'Название должно быть от 1 до 50 символов'
            : 'Не удалось переименовать ключ';
      toast.error(getApiErrorMessage(err, fallback));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        {editing ? (
          <div className={styles.nameEditRow}>
            <input
              className={styles.nameInput}
              type="text"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveName();
                } else if (e.key === 'Escape') {
                  cancelEdit();
                }
              }}
              maxLength={50}
              disabled={saving}
              autoFocus
              aria-label="Название ключа"
            />
            <button
              type="button"
              className={`${styles.nameActionBtn} ${styles.nameSaveBtn}`}
              onClick={handleSaveName}
              disabled={saving}
              aria-label="Сохранить название"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.nameActionBtn}
              onClick={cancelEdit}
              disabled={saving}
              aria-label="Отменить переименование"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        ) : (
          <div className={styles.titleRow}>
            {vpnKey.device && (
              <span className={styles.boundIcon} title="Ключ привязан к устройству" aria-label="Ключ привязан к устройству">
                <PluggedIcon size={18} />
              </span>
            )}
            <span className={styles.title}>
              {vpnKey.device?.title || vpnKey.deviceName || 'VPN-ключ'}
            </span>
            <button
              type="button"
              className={styles.editBtn}
              onClick={startEdit}
              aria-label="Переименовать ключ"
              title="Переименовать"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
              </svg>
            </button>
          </div>
        )}
        {!editing && (
          <Badge variant={vpnKey.isActive ? 'success' : 'error'}>
            {vpnKey.isActive ? 'Активен' : 'Неактивен'}
          </Badge>
        )}
      </div>

      <div className={styles.urlContainer}>
        <span className={styles.url}>{vpnKey.subscriptionUrl}</span>
        <button className={styles.copyBtn} onClick={handleCopy}>
          {copied ? 'Скопировано!' : 'Копировать'}
        </button>
      </div>

      <KeyDeviceBlock device={vpnKey.device} />

      <div className={styles.actions}>
        {vpnKey.device ? (
          // Устройство уже подключено: вместо «Подключить» — отвязка
          <ConnectButton connected loading={unbinding} onClick={() => void handleUnbind()} />
        ) : (
          <ConnectButton
            onClick={() => {
              // Копируем в самом обработчике клика: Safari пишет в буфер только внутри жеста
              void copyText(vpnKey.subscriptionUrl).then(setAutoCopied);
              setConnectOpen(true);
            }}
          />
        )}
      </div>

      <ConnectModal
        isOpen={connectOpen}
        copied={autoCopied}
        onClose={() => setConnectOpen(false)}
        subscriptionUrl={vpnKey.subscriptionUrl}
        title={
          vpnKey.device?.title || vpnKey.deviceName
            ? `Подключить — ${vpnKey.device?.title || vpnKey.deviceName}`
            : 'Подключиться'
        }
      />
    </div>
  );
}
